"use client";

import { useTranslations } from "next-intl";

import { ButtonLink } from "@/components/ButtonLink";
import {
  homeStudioBudgetFitOtp,
  homeStudioBudgetFitTrust,
  WEBSITE_VOC_MESSAGING_FLAG,
} from "@/content/experiments";
import type { HomeStudioPersonaCopy } from "@/content/experiments/copy";
import { rolePaths } from "@/content/role-paths";
import { Link } from "@/i18n/navigation";
import { useExperimentVariant } from "@/lib/analytics/experiments";
import {
  EXPERIMENT_OTP_VARIANT,
  EXPERIMENT_TRUST_VARIANT,
} from "@/lib/analytics/experiments.constants";
import {
  FILM_SECTION_CLASS,
  SectionSlate,
  SplitTitle,
} from "@/components/Film";

import { FILM_CALL_TIMES } from "../home-film.constants";

const studioCopyForVariant = (
  variant: string,
): HomeStudioPersonaCopy | null => {
  if (variant === EXPERIMENT_OTP_VARIANT) return homeStudioBudgetFitOtp;
  if (variant === EXPERIMENT_TRUST_VARIANT) return homeStudioBudgetFitTrust;
  return null;
};

type CallSheetProps = {
  projectTitle: string;
};

/**
 * 05 · Crew call. The personas laid out like the crew list on a call sheet:
 * role, call time, what RivetAI hands them. Layout only; the copy never claims
 * RivetAI produces call sheets.
 */
export const CallSheet = ({ projectTitle }: CallSheetProps) => {
  const t = useTranslations("home.rolePaths");
  const film = useTranslations("home.film");
  const variant = useExperimentVariant(WEBSITE_VOC_MESSAGING_FLAG);
  const studioCopy = studioCopyForVariant(variant);

  return (
    <section
      aria-labelledby="film-call-sheet-title"
      className={`${FILM_SECTION_CLASS} py-20 lg:py-28`}
    >
      <div className="marketing-grid flex flex-col gap-10">
        <div className="flex max-w-2xl flex-col gap-3">
          <SectionSlate index={5} label={film("slates.callSheet")} />
          <SplitTitle
            id="film-call-sheet-title"
            className="font-display text-3xl font-semibold tracking-tight text-balance sm:text-4xl"
          >
            {t("title")}
          </SplitTitle>
          <p className="text-muted-foreground">{t("subtitle")}</p>
        </div>

        <div className="overflow-hidden rounded-lg border border-border bg-card dark:bg-[var(--marketing-dark-card)]">
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground sm:px-8">
            <span>{film("callSheet.header", { project: projectTitle })}</span>
            <span className="tabular-nums">{film("callSheet.day")}</span>
          </div>

          {/* Visual column heads; each row carries its own screen reader labels. */}
          <div
            aria-hidden
            className="hidden grid-cols-[14rem_5rem_minmax(0,1fr)_auto] gap-6 border-b border-border px-8 py-2 text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground lg:grid"
          >
            <span>{film("callSheet.columns.role")}</span>
            <span>{film("callSheet.columns.call")}</span>
            <span>{film("callSheet.columns.handoff")}</span>
          </div>

          <ul>
            {rolePaths.map((path) => {
              const copy = path.id === "studio" ? studioCopy : null;
              const bullets = [1, 2, 3].map((index) =>
                copy
                  ? [copy.bullet1, copy.bullet2, copy.bullet3][index - 1]
                  : t(`${path.id}.bullet${index}`),
              );
              return (
                <li
                  key={path.id}
                  className="grid gap-4 border-b border-border px-5 py-6 last:border-b-0 sm:px-8 lg:grid-cols-[14rem_5rem_minmax(0,1fr)_auto] lg:items-start lg:gap-6"
                >
                  <div className="flex flex-col gap-1">
                    <p className="font-display text-lg font-semibold tracking-tight">
                      {film(`callSheet.roles.${path.id}`)}
                    </p>
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                      {t(
                        `${path.id === "writer" ? "writerTab" : path.id === "studio" ? "studioTab" : "enterpriseTab"}`,
                      )}
                    </p>
                  </div>
                  <p className="font-display text-lg font-semibold tabular-nums">
                    <span className="sr-only">
                      {film("callSheet.columns.call")}:{" "}
                    </span>
                    {FILM_CALL_TIMES[path.id]}
                  </p>
                  <div className="flex flex-col gap-2">
                    <p className="font-semibold">
                      {copy ? copy.headline : t(`${path.id}.headline`)}
                    </p>
                    <ul className="flex flex-col gap-1.5 text-sm text-muted-foreground">
                      {bullets.map((bullet) => (
                        <li key={bullet} className="flex gap-2">
                          <span aria-hidden>·</span>
                          {bullet}
                        </li>
                      ))}
                    </ul>
                    <Link
                      href={path.learnMoreHref}
                      className="w-fit text-sm font-medium underline-offset-4 hover:underline"
                    >
                      {t(`${path.id}.learnMore`)}
                    </Link>
                  </div>
                  <ButtonLink
                    href={path.ctaHref}
                    variant="outline"
                    className="w-fit"
                    trackingLocation={`home_persona_${path.id}`}
                  >
                    {t(`${path.id}.cta`)}
                  </ButtonLink>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
};
